"use client";

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { getSession } from "@/lib/api/team";
import { useOptionalAuth } from "@/lib/auth/auth-context";
import { getRole } from "@/lib/rbac/roles";
import { API_MODE } from "@/lib/api/helpers";
import { readEffective, type Effective } from "@/lib/rbac/effective";
import { decide, deniedReason } from "@/lib/rbac/platform-gate";
import type { Permission } from "@/lib/rbac/permissions";
import type { Member, Organisation, Project } from "@/lib/api/types";

/**
 * The tenancy scopes the console can be "in", named as RAD §8.1 names them:
 *
 *   Organisation — the tenant commercial and isolation boundary
 *   Project      — the primary unit of work, owning targets, scans and findings
 *
 * RAD §2 puts a **Workspace** (a team partition) between the two, and FR-04
 * requires CRUD for it. This console has no such tier: a Project hangs directly
 * off an Organisation (`Project.orgId`). That gap is recorded rather than
 * papered over — adding the tier is a structural change, not a rename.
 */
interface TenancyContextValue {
  loading: boolean;
  /**
   * The session read FAILED — which is not "no projects". A page that renders
   * an error as absence tells a signed-in person their organisation is empty
   * (S-246 found exactly that, from a session query that ran before the token
   * existed). Pages show this, in the platform's words, instead of an empty list.
   */
  error?: Error;
  user?: Member;

  /**
   * THE organisation. Singular, and there is no setter.
   *
   * S-177, RULED 2026-08-27: "one organisation per login. A person working with
   * two customers holds two logins, one from each organisation's invitation."
   * The register names what that removes — "no set in the token, no active-org
   * choice, no switcher" — so a list, a selector and a stored active id are all
   * capability this console must not have.
   *
   * The RAD is UNAMENDED: RAD §8.2.4 still says "many-to-many via membership"
   * while RAD §8.3 draws one-to-many. This follows the ruling, and the
   * amendment is owed through change control.
   */
  organisation?: Organisation;
  organisationId: string;

  /** Projects within the active organisation. */
  projects: Project[];
  /** The active project. */
  project?: Project;
  projectId: string;
  setProjectId: (id: string) => void;

  permissions: Set<Permission>;
  /**
   * Whether this console knows what the caller may do.
   *
   * 2.795, SPONSOR RULING (2026-10-02, relayed by the coordinator): over HTTP it
   * is TRUE once the platform's answer about the caller's own permissions
   * (`GET /v1/people/{sub}/permissions`, S-709, ADR-106) has been read AND
   * parsed. Anything else — loading, a 503, a network error, a malformed
   * answer, a 404 for oneself — leaves it FALSE, and every control is shown for
   * the platform to refuse (fail-open on a FAILED read, as before).
   *
   * The mock fixture knows from its memberships, unchanged.
   */
  permissionsKnown: boolean;
  /**
   * Whether to SHOW a permission-gated control. The ONE place that decides, so
   * the sidebars, the palette and `<Can>` cannot disagree.
   *
   * `projectId` scopes a project capability to that project; without one, over
   * HTTP, holding it on any project is enough (`lib/rbac/platform-gate.ts`). A
   * permission the platform's answer cannot decide is always shown.
   */
  can: (permission: Permission, projectId?: string) => boolean;
  /**
   * Why `can` said no, in the platform's terms — for a control shown DISABLED
   * rather than hidden. Undefined whenever `can` says yes.
   */
  whyNot: (permission: Permission, projectId?: string) => string | undefined;
}

const Ctx = React.createContext<TenancyContextValue | undefined>(undefined);

export function TenancyProvider({ children }: { children: React.ReactNode }) {
  /**
   * The session is read only once there is a token to read it with.
   *
   * On a reload the token is restored silently against Keycloak (a redirect
   * out and back), and this provider mounts BEFORE that completes. The query
   * used to fire at mount regardless: `getSession` refused ("called before
   * anyone is signed in"), react-query retried once a second later, and if the
   * restore took longer than that the error was final — nothing re-ran it when
   * the token arrived, and every page then showed "No project is visible to
   * this login" for a login with two projects. MEASURED 2026-09-05.
   *
   * So the query is gated on the auth status, and keyed by the subject so a
   * different sign-in is a different session rather than a cached one. With no
   * auth provider around (the mock source, the tests) nothing is waited for.
   */
  const auth = useOptionalAuth();
  const ready = !auth || auth.status === "authenticated" || auth.status === "unconfigured";
  const { data, isLoading, error } = useQuery({
    queryKey: ["session", auth?.claims?.sub ?? "local"],
    queryFn: getSession,
    enabled: ready,
  });
  const [projectId, setProjectIdState] = React.useState("");

  // The organisation comes from the session and is never chosen. Nothing is
  // stored, because there is nothing to remember: one login, one organisation.
  const organisation = data?.organisation;
  const organisationId = organisation?.id ?? "";

  /**
   * The active project is driven by the route — `ProjectShell` calls this with
   * the `:projectId` segment. Deliberately NOT persisted: the URL is the source
   * of truth, and the stored copy that nothing ever read back is removed here.
   */
  const setProjectId = React.useCallback((id: string) => setProjectIdState(id), []);

  const projects = React.useMemo(
    () => (data?.projects ?? []).filter((p) => p.orgId === organisationId),
    [data, organisationId],
  );

  // The mock fixture's memberships, unchanged. Over HTTP there are none.
  const membershipsKnown = (data?.user?.memberships?.length ?? 0) > 0;

  // 2.795: the caller's OWN permissions, as the platform decides them, read
  // once per signed-in subject and organisation. The answer covers every
  // project of the organisation, so a project switch decides again from it
  // rather than reading again. Over HTTP only: mock mode keeps its memberships.
  const selfId = data?.user?.id ?? "";
  const self = useQuery({
    queryKey: ["self-permissions", selfId, organisation?.id ?? ""],
    queryFn: () => readEffective(selfId),
    enabled: API_MODE === "http" && !membershipsKnown && !!selfId && !!organisation,
  });
  // Only a PARSED answer about this subject counts; readEffective throws on anything else.
  const answer: Effective | undefined = self.data && !self.error ? self.data : undefined;
  const permissionsKnown = membershipsKnown || !!answer;

  const permissions = React.useMemo(() => {
    const set = new Set<Permission>();
    if (!data?.user) return set;
    // The membership for the active project if there is one, else the user's first.
    const membership =
      data.user.memberships.find((m) => m.projectId === projectId) ?? data.user.memberships[0];
    if (!membership) return set;
    getRole(membership.roleId)?.permissions.forEach((p) => set.add(p));
    return set;
  }, [data, projectId]);

  const value: TenancyContextValue = {
    // Not yet allowed to ask is "loading", not "empty".
    loading: isLoading || (!ready && !data),
    error: error instanceof Error ? error : error ? new Error(String(error)) : undefined,
    user: data?.user,
    organisation,
    organisationId,
    projects,
    project: projects.find((p) => p.id === projectId),
    projectId,
    setProjectId,
    permissions,
    permissionsKnown,
    can: (permission, scope) => {
      if (!permissionsKnown) return true;
      if (membershipsKnown) return permissions.has(permission);
      // Unmapped is `undefined`: shown, never hidden on the absence of a mapping.
      return decide(answer!, permission, scope) !== false;
    },
    whyNot: (permission, scope) => {
      if (!permissionsKnown || membershipsKnown || !answer) return undefined;
      return decide(answer, permission, scope) === false ? deniedReason(permission, scope) : undefined;
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTenancy() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useTenancy must be used within TenancyProvider");
  return ctx;
}
